import React, { useEffect, useState } from 'react';
import { Lock, LogOut, Mail, Phone, Users, Calendar, RefreshCw, FileText, Images } from 'lucide-react';
import AdminContent from './AdminContent';

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls =
  'w-full bg-ivory-soft border border-emerald/15 rounded-sm px-4 py-3.5 text-sm text-emerald-ink placeholder:text-emerald-ink/40 outline-none transition-colors duration-300 focus:border-terracotta focus:ring-2 focus:ring-terracotta/30';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Qualcosa è andato storto. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || '').filter(Boolean).join(' ');
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem('g28-admin-token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState(null);
  const [tab, setTab] = useState('requests');

  const loadRequests = async (tok) => {
    const res = await fetch(`${API}/api/contacts`, { headers: { Authorization: `Bearer ${tok}` } });
    if (res.status === 401) {
      localStorage.removeItem('g28-admin-token');
      setToken(null);
      return;
    }
    setRequests(await res.json());
  };

  useEffect(() => {
    if (token) loadRequests(token);
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      localStorage.setItem('g28-admin-token', data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem('g28-admin-token');
    setToken(null);
    setRequests(null);
  };

  if (!token) {
    return (
      <div className="min-h-[100svh] bg-ivory flex items-center justify-center px-6 pt-20" data-testid="admin-login-page">
        <form onSubmit={onLogin} data-testid="admin-login-form" className="w-full max-w-md bg-white border border-emerald/10 rounded-sm p-10 shadow-[0_8px_40px_rgba(37,79,87,0.08)]">
          <div className="flex justify-center">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-ivory text-emerald">
              <Lock size={24} strokeWidth={1.5} />
            </span>
          </div>
          <h1 className="mt-6 text-center font-serif text-3xl text-emerald">Area riservata</h1>
          <p className="mt-2 text-center text-sm text-emerald-ink/60">Accedi per vedere le richieste ricevute</p>
          <div className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">Email</label>
              <input id="admin-email" data-testid="admin-input-email" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs uppercase tracking-[0.2em] text-emerald/60 mb-2">Password</label>
              <input id="admin-password" data-testid="admin-input-password" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full rounded-full bg-emerald text-ivory py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:bg-terracotta-dark disabled:opacity-60"
            >
              {loading ? 'Accesso in corso…' : 'Accedi'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] bg-ivory pt-28 pb-20 px-6 md:px-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-emerald">Area riservata</h1>
            <div className="mt-4 flex gap-2" data-testid="admin-tabs">
              <button
                data-testid="admin-tab-requests"
                onClick={() => setTab('requests')}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-colors duration-300 ${tab === 'requests' ? 'bg-emerald text-ivory' : 'bg-white border border-emerald/15 text-emerald hover:border-terracotta'}`}
              >
                <FileText size={14} /> Richieste{requests ? ` (${requests.length})` : ''}
              </button>
              <button
                data-testid="admin-tab-content"
                onClick={() => setTab('content')}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm transition-colors duration-300 ${tab === 'content' ? 'bg-emerald text-ivory' : 'bg-white border border-emerald/15 text-emerald hover:border-terracotta'}`}
              >
                <Images size={14} /> Contenuti sito
              </button>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => loadRequests(token)}
              className="inline-flex items-center gap-2 rounded-full border border-emerald/20 text-emerald px-5 py-2.5 text-sm transition-colors duration-300 hover:border-terracotta hover:text-terracotta-dark"
            >
              <RefreshCw size={15} /> Aggiorna
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-emerald text-ivory px-5 py-2.5 text-sm transition-colors duration-300 hover:bg-terracotta-dark"
            >
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>

        {tab === 'requests' && (
        <div className="mt-10 space-y-4" data-testid="admin-requests-list">
          {requests && requests.length === 0 && (
            <p className="text-emerald-ink/60 text-sm bg-white border border-emerald/10 rounded-sm p-8">Nessuna richiesta per ora.</p>
          )}
          {requests && requests.map((r) => (
            <article key={r.id} data-testid={`admin-request-${r.id}`} className="bg-white border border-emerald/10 rounded-sm p-6 md:p-8">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-2xl text-emerald">{r.name}</h2>
                  <p className="text-sm text-terracotta-dark font-medium mt-1">{r.event_type}{r.guests ? ` · ${r.guests} ospiti` : ''}</p>
                </div>
                <span className="text-xs text-emerald-ink/50 inline-flex items-center gap-2">
                  <Calendar size={13} />
                  {new Date(r.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  <span className="uppercase tracking-widest">({r.language})</span>
                </span>
              </div>
              <p className="mt-4 text-sm text-emerald-ink/70 leading-relaxed whitespace-pre-line">{r.message}</p>
              <div className="mt-5 flex flex-wrap gap-4 text-sm">
                <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 text-emerald hover:text-terracotta-dark transition-colors duration-300">
                  <Mail size={15} /> {r.email}
                </a>
                {r.phone && (
                  <a href={`tel:${r.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 text-emerald hover:text-terracotta-dark transition-colors duration-300">
                    <Phone size={15} /> {r.phone}
                  </a>
                )}
                {r.guests && <span className="inline-flex items-center gap-2 text-emerald-ink/50"><Users size={15} /> {r.guests}</span>}
              </div>
            </article>
          ))}
        </div>
        )}
        {tab === 'content' && (
          <div className="mt-10">
            <AdminContent token={token} />
          </div>
        )}
      </div>
    </div>
  );
}
